import { useEffect, useRef, useState } from 'react';
import { Badge, Button } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import {
  linkOf,
  rowPushDecision,
  rowPushFailure,
  rowPushReport,
  statusLabel,
  statusTone,
  updatePushCaution,
  xeroErrorLines,
  type MaybeLinked,
  type XeroEntity,
  type XeroRowPushReport,
  type XeroTone,
} from '@/lib/xero';

/**
 * The Xero push control on a LIST ROW — `/quotes` and `/invoices`.
 *
 * ## Why one component, not two blocks of JSX
 *
 * The two pages must not word or gate this differently — splitting it is how
 * they diverge (a refusal has already shipped described in two tones across
 * the two pages). Every string and gate it renders — label, enable/disable
 * decision, disabled sentence, confirmation copy, outcome report — is decided
 * in `@/lib/xero`. A page supplies only the record and a place for the banner.
 *
 * ## ⚠️ CORRECTED 2026-08-31 — ON A QUOTE OR AN INVOICE THERE IS NO BUTTON AT ALL
 *
 * This section read:
 *
 * > *"## The three states*
 * > *- **Sync to Xero** — linked, and writable. ⚠️ Not "newer than Xero" — this*
 * > *  app cannot tell.*
 * > *- **Disabled** — with a sentence naming which provable reason applies.*
 * > *- **No button at all** — the record is not in Xero yet. Saving it is what*
 * > *  puts it there; see below."*
 *
 * A first pass on 2026-08-30 took the CREATE half, on the owner's decision that
 * *"on quotes and invoices the data is directly getting synced to Xero … remove
 * the Push to Xero button on both"* — and kept the manual UPDATE, on the
 * argument that overwriting a draft you already pushed is low-stakes.
 *
 * **The owner has overruled that, 2026-08-31, having asked twice**: *"I told you
 * multiple times: remove this Push to Xero button from both invoices and quotes,
 * because auto saving is working."* He screenshotted this control on `/quotes`,
 * showing the **Sync to Xero** branch. So the update goes too, and on those two
 * entities this control renders a status badge and one sentence.
 *
 * ## What the entity gate is, and why it is a `Record`
 *
 * {@link MANUAL_SYNC_OFFERED} — the same shape and the same reason as
 * `CREATED_BY_SAVING` in `XeroPanel`. This component is SHARED, and the branch
 * being withheld is a property of the ENTITY, not of this surface: a quote or
 * an invoice reaches Xero by being saved, so a manual re-send is a second way
 * to do what the save already did. A `Record` over the entity union means a
 * fifth entity has to be classified before this file compiles, where an
 * `entity === 'quotes' ||` test would silently default it to "still a button".
 *
 * ⚠️ **`companies` MUST STAY `true`.** A company is NOT pushed to Xero by
 * being created — the server enqueues nothing on `createCompany` — so the
 * manual push is the only route a customer has into the accounting system, and
 * every quote and invoice depends on the company being there first. This
 * control has no company caller today; the entry is what stops the gate being
 * read as "manual pushes are gone" if one is ever added.
 *
 * ## 🔴 THE GAP THIS LEAVES, WRITTEN DOWN BECAUSE IT IS A DECISION
 *
 * **There is now no manual retry for a quote or an invoice whose automatic push
 * failed.** The way to retry is to save the document again — a save re-pushes,
 * which is exactly why the button went. But a document whose push failed and
 * which nobody edits again **stays stale in Xero, with nothing on screen
 * offering to resend it**. Accepted, not overlooked. Reported to the owner.
 *
 * ⚠️ **`POST /xero/create/{entity}/{id}` is untouched** and stays as the
 * operator recovery path. This removes the affordance, not the capability —
 * and `pushIntentFor` still mints `'create'`, which is what the two form
 * modals use when a person presses Create on the form itself.
 *
 * ## ⚠️ A linked row is never disabled for "nothing to send"
 *
 * There is no client-derivable "nothing to send" test — an edit to
 * `description`, `cityIds` or `inclusions` reaches the customer's Xero
 * document while moving no timestamp this app can read, so a freshness-based
 * disabled state is a false claim. `rowPushDecision` (layered on
 * `pushIntentFor`, the single create-vs-update decision point) never
 * re-derives this from `updatedAt` — `hasUnsentChanges` in `@/lib/xero` is
 * the one place the backend's `stale` flag will be read once it lands.
 *
 * ⚠️ When in doubt, ENABLE. A button wrongly enabled costs one redundant push
 * of an identical payload; wrongly disabled is a false statement that leaves
 * a stale document in the accounts.
 *
 * ## ⚠️ NOTHING CONFIRMS ANY MORE, AND THAT IS THE CREATE LEAVING
 *
 * This section read:
 *
 * > *"## ⚠️ Only a CREATE confirms — A **create** opens `ConfirmDialog` — it
 * > puts a new document into the accountant's Xero that nothing in EMERGE can
 * > withdraw. An **update** fires on the click."*
 *
 * The create branch is gone, so the only thing that opened the dialog is gone
 * with it and the dialog is deleted rather than left unreachable. **The update
 * half is unchanged and was never confirmed**: overwriting a draft you already
 * pushed is low-stakes, and confirming every time is wearing. The warning the
 * dialog used to carry ("a draft is replaced silently, with no further
 * warning") still lives in two places, both in `@/lib/xero` so the two pages
 * can't word it differently:
 *
 * - `updatePushCaution`, rendered under the button, before the click, in the
 *   slot the disabled reason occupies — never a `title` (unreachable on a
 *   disabled button, and on touch). A row shows the disabled reason or the
 *   caution, never both.
 * - the `pushed` + `update` line in `rowPushReport`, after the click, naming
 *   what was just replaced.
 *
 * ⚠️ **Both are now unreachable from `/quotes` and `/invoices`** — there is no
 * click to warn about and no report to render — and both are kept because they
 * are the copy for an entity {@link MANUAL_SYNC_OFFERED} still admits. They are
 * in `@/lib/xero`, which this work does not own; nothing here should delete
 * them on the strength of this surface no longer reaching them.
 *
 * ## Deliberately NOT built
 *
 * No bulk, no select-all, no "push all" — a list of linked rows is already a
 * column of one-click writes to the accounting system, and select-all would
 * write to all of them at once. No retry either: every entry in
 * `XERO_QUOTE_REFUSALS` types `retryable` as the literal `false`.
 */

/**
 * Which entities still offer a MANUAL push from a list row.
 *
 * Owner's decision, 2026-08-31, asked twice: *"remove this Push to Xero button
 * from both invoices and quotes, because auto saving is working."* The server
 * pushes inside every save of a quote or an invoice — creating the Xero
 * document on the first save and updating it on every one after — so a button
 * here is a person doing by hand what the save already did.
 *
 * ⚠️ **`companies: true` is not symmetry waiting to be finished.** A company
 * is not pushed on create (`createCompany` enqueues nothing, deliberately: a
 * CRM row may be a typo, a duplicate or a test record, and there is no undo in
 * Xero a CRM delete can reach), so a manual push is the ONLY route a customer
 * has into the accounting system. Flipping this entry would take it away, and
 * every quote and invoice push depends on the company being linked first.
 *
 * ⚠️ **`contacts: false` is a different fact and must not be read as this
 * decision.** A Xero `ContactPerson` has no GUID of its own, so a contact is
 * never linked or pushed individually at all — `contacts.capabilities` are all
 * false in `@/lib/xero`. It is `false` here because there is nothing to push,
 * not because a save pushes it.
 *
 * An exhaustive `Record` rather than a `Set` or an `entity === 'quotes' ||`
 * test: a fifth entity has to be classified before this file compiles.
 */
const MANUAL_SYNC_OFFERED: Record<XeroEntity, boolean> = {
  companies: true,
  contacts: false,
  quotes: false,
  invoices: false,
};

/**
 * What stands where the button was.
 *
 * ⚠️ **Reused verbatim from the 2026-08-30 pass, not reworded.** It was already
 * the sentence an unlinked row carried, and it is true of a linked one for the
 * same reason — the save is what sends it, first time and every time. A second
 * phrasing for one fact is how two lists come to describe auto-push
 * differently. A control that simply vanishes reads as broken; this is the one
 * line that says it did not.
 */
const SAVE_SENDS_IT = 'Goes to Xero when you save it.';

export interface XeroRowPushControlProps {
  entity: XeroEntity;
  /** The record's own id — the `{id}` in `POST /xero/create/{entity}/{id}`. */
  id: number;
  /** What to call it in the confirmation and the report. */
  name: string;
  /**
   * The record exactly as the API sent it. The link is read off its `xero`
   * envelope through `linkOf`, never off `xeroQuoteId` / `xeroInvoiceId` — both
   * of those columns are writable and written by nothing.
   *
   * Typed `MaybeLinked` rather than `object`: `Quote` and `Invoice` both declare
   * `xero` now, so the envelope is reachable through the type system and a
   * caller passing something that carries no link fails the build here instead
   * of silently reading as unlinked at runtime. `linkOf` still answers
   * `NO_LINK` for a `xero` that is absent or null, so a response predating the
   * envelope degrades rather than crashing.
   */
  record: MaybeLinked;
  /**
   * ⚠️ ACCEPTED AND DELIBERATELY UNREAD — see the "nothing to send" note in
   * this file's header. No timestamp available to this app is moved by the
   * line edits that change what the customer receives, so nothing here
   * should start reading it. Stays on the interface only because
   * `/quotes` and `/invoices` both still pass it.
   */
  updatedAt?: string | null | undefined;
  /**
   * `cell` is the desktop table column; `card` is the sub-`md` stacked list,
   * where the control sits in a row of full-label buttons.
   */
  variant: 'cell' | 'card';
  /**
   * Called once the push has settled, however it settled, with the report to
   * render. The page also re-reads the row on this — **the push writes the link
   * row before it can refuse**, so even a refusal can have moved the state.
   */
  onSettled: (report: XeroRowPushReport) => void;
}

export function XeroRowPushControl({
  entity,
  id,
  name,
  record,
  variant,
  onSettled,
}: XeroRowPushControlProps) {
  const { ability } = useAuth();
  const [busy, setBusy] = useState(false);

  /*
   * ⚠️ **The double-fire guard.** `busy` is state and lags a rapid second click
   * by a render, so `disabled={busy}` cannot be the guard; a ref is read
   * synchronously in the same tick.
   *
   * ⚠️ It matters LESS than it did and is kept anyway. It read: *"a double
   * click is one click away from two pushes, which on a create is two documents
   * in the accountant's Xero."* No click here creates any more, so the worst a
   * double click can now do is send the same update twice — but the server's
   * `pushInvoice` has no per-invoice lock, so two overlapping updates both
   * write and last one wins, silently.
   *
   * Same pattern and same reason as `XeroPanel`, one file over.
   */
  const inFlight = useRef(false);

  /*
   * `api.pushToXero` takes no `AbortSignal` — the method predates this control
   * and lives in a file this work does not own — so the usual `signal.aborted`
   * guard is unavailable and this stands in for it. It guards STATE ONLY: the
   * request itself cannot be recalled, and a push that has left the browser has
   * already reached the accounting system whatever this component does next.
   */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /*
   * Gate on the ability, never on the role and never on `/permissions/me`, which
   * over-reports. `denialReason` gives the TRUE reason rather than "administrators
   * only", which is wrong for a viewer. A hidden or disabled control is only an
   * optimisation — a role can change under a live access token, which lasts over
   * five days — so the 403 branch in `rowPushFailure` is the actual guarantee.
   */
  /*
   * 2026-10-02 (owner): a Xero push is `<module>.send`. `contacts` carries no
   * `send` (a contact is never pushed on its own), so it keeps `update`.
   */
  const gateAction = entity === 'contacts' ? 'update' : 'send';
  const denied = ability.can(gateAction, entity)
    ? null
    : /*
       * `denialReason` returns null only when the ability says yes, which this
       * branch has already ruled out — the fallback covers the case the client
       * cannot explain rather than letting a disabled control say nothing.
       */
      (denialReason(ability, gateAction, entity) ?? 'You do not have permission for this.');

  const link = linkOf(record);
  const decision = rowPushDecision(entity, link, denied);

  /*
   * ⚠️ **The create is not offered from a list any more** — see the correction
   * in this file's header. `rowPushDecision` still ANSWERS `'create'`, and
   * deliberately: it is the shared model, the two form modals mint their intent
   * through the same path, and narrowing it there would take the create away
   * from the one place the owner kept it. What is decided here is only whether
   * this control renders a button, which is a property of this surface.
   *
   * ⚠️ **Keyed on the LINK, not on `decision.action`, and the difference is a
   * real state.** `decision.action` exists only while a decision is ENABLED, so
   * gating on it leaves every *disabled* row with no Xero id still rendering a
   * dead button captioned "Push to Xero" — which is reachable today by any
   * `viewer` on `/quotes`, since the permission refusal is checked before the
   * link is. `link.id === null` is the same thing `rowPushDecision` derives its
   * own label from, so the button and the label cannot disagree about which
   * side of the line a row sits on.
   */
  const inXero = link.id !== null;
  /*
   * ⚠️ **CORRECTED 2026-08-31.** This read `const offersButton = inXero;` under
   * a comment explaining that keying on the LINK rather than on
   * `decision.action` was what stopped a disabled unlinked row rendering a dead
   * "Push to Xero". Both halves of that are now moot on the two entities this
   * control actually serves: {@link MANUAL_SYNC_OFFERED} withholds the button
   * whatever the link says, so there is no create branch AND no update branch
   * to key on. The link test is kept for the entity that still has one — a
   * company with no Xero id is created from `XeroPanel`, not from a list row.
   */
  const syncsOnSave = !MANUAL_SYNC_OFFERED[entity];
  const offersButton = inXero && !syncsOnSave;

  /*
   * The one sentence in the note slot, decided here rather than in the JSX so
   * two states cannot both render and so the reserved height below has a single
   * occupant.
   *
   * ⚠️ **The save sentence WINS over everything, including a permission
   * reason** — on a quote or an invoice it is the only true thing this cell can
   * say. There is no control to be refused, so naming a permission explains
   * something the reader cannot see, and a SAVE is not gated the same way: a
   * `viewer` cannot save either, but the sentence describes the document rather
   * than the reader.
   *
   * ⚠️ It is deliberately the SAME STRING for a linked row as for an unlinked
   * one, because the fact is the same one: the save is what sends it, first
   * time and every time. Two phrasings of one fact is how the two lists come to
   * describe auto-push differently.
   *
   * The `!inXero` branch below is unreachable today — no caller passes an
   * entity that both offers the button and can be unlinked — and answers `null`
   * rather than borrowing the save sentence, which would be false of a company.
   */
  const note: string | null = syncsOnSave
    ? SAVE_SENDS_IT
    : !inXero
      ? null
      : !decision.enabled
        ? decision.reason
        : decision.action === 'update'
          ? updatePushCaution(entity)
          : null;

  /*
   * A plain function, deliberately NOT a `useCallback`. It closes over
   * `decision`, which is derived at render and is a fresh object every time, so
   * a manual memo could never be preserved — oxlint's
   * `react(preserve-manual-memoization)` says exactly that. The React Compiler
   * memoizes this on its own terms; hand-written memoization here would be a
   * dependency list to keep in step by hand for no gain.
   */
  async function run() {
    if (!decision.enabled) return;
    /*
     * Read and set in one synchronous step, before the first `await`, so a second
     * click that arrives before React has re-rendered the disabled button finds
     * the flag already up and returns without starting a second request. Cleared
     * in `finally` — unconditionally, and NOT behind `mounted`, or an unmount
     * mid-flight would leave the flag raised on a remounted row.
     */
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      /*
       * The intent is minted by `pushIntentFor` inside `rowPushDecision` and is
       * branded, so it cannot be written down by hand at this call site — it can
       * only have been derived from the link, which is the same thing the server
       * derives create-vs-update from.
       */
      await api.pushToXero(entity, id, decision.intent);
      if (!mounted.current) return;
      onSettled(rowPushReport(entity, name, { kind: 'pushed', action: decision.action }));
    } catch (caught: unknown) {
      if (!mounted.current) return;
      if (caught instanceof ApiError) {
        /*
         * The same line extraction the detail pages use, character for character:
         * Xero's real objection is nested and its top-level wrapper says only
         * "A validation exception occurred", which `xeroErrorLines` drops when
         * something specific survives it.
         */
        const messages = xeroErrorLines([caught.message, ...caught.xeroMessages], caught.message);
        onSettled(rowPushReport(entity, name, rowPushFailure(caught, messages)));
      } else {
        /*
         * No answer at all — a dropped connection. NOTHING is known about Xero's
         * copy: the request may well have landed. `unreachable` is the one outcome
         * whose copy refuses to describe Xero's state.
         */
        onSettled(
          rowPushReport(entity, name, {
            kind: 'unreachable',
            messages: [caught instanceof Error ? caught.message : 'The request did not complete.'],
          }),
        );
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  return (
    <>
      <div
        className={cx(
          'flex min-w-0 gap-1.5',
          variant === 'cell' ? 'flex-col items-start' : 'flex-col items-end',
        )}
      >
        {/*
          * The state badge is not decoration.
          *
          * ⚠️ **CORRECTED 2026-08-31.** It read: *"and is not redundant with the
          * label. The label says what the button WOULD do; the badge says where
          * the record stands — and the two disagree in exactly the cases that
          * matter, a `failed` link most of all, which reads 'Failed' beside an
          * enabled 'Sync to Xero'."* There is no label to disagree with on a
          * quote or an invoice any more, which makes the badge carry the whole
          * state on its own rather than half of it. Six statuses collapse to
          * four tones, so the word is always present: colour is never the only
          * signal. A `failed` link now reads "Failed" beside a sentence saying
          * the next save is what will re-send it, which is the truth.
          */}
        <span className="inline-flex" title={statusLabel(link.status)}>
          <Badge tone={statusTone(link.status)} dot>
            {statusLabel(link.status)}
          </Badge>
        </span>

        {/* Full-width in the table cell so the states line up down the column;
            natural width in the card, where it sits in a button row.
            `justify-center` is already in `Button`'s base.

            ⚠️ ABSENT rather than disabled — and since 2026-08-31 absent on
            EVERY row of `/quotes` and `/invoices`, not only on a would-be
            create. Nothing on either page can ever enable it: the record
            reaches Xero by being saved, first time and every time. A control
            that can only ever be dead is the shape this repo's own rule
            refuses ("the affordance must be ABSENT, not merely inert"). The
            note below carries the sentence instead.

            ⚠️ 🔴 **This is where the manual retry used to be, and there is now
            no replacement for it.** A quote or invoice whose automatic push
            failed is re-sent by saving it again — but one nobody edits again
            stays stale in Xero with nothing on screen offering to resend it.
            Accepted on the owner's decision of 2026-08-31, not overlooked. */}
        {offersButton && (
          <Button
            variant="secondary"
            size="sm"
            className={variant === 'cell' ? 'w-full' : undefined}
            disabled={!decision.enabled || busy}
            loading={busy}
            /*
             * ⚠️ **CORRECTED 2026-08-30.** This read: *"The two branches part
             * here and nowhere else. A CREATE opens the dialog; an UPDATE starts
             * the request on this click."* There is one branch now — every click
             * that reaches this handler is an UPDATE, because `offersButton`
             * withholds the button on the other one.
             *
             * `disabled` is not the double-fire guard — it lags by a render. The
             * guard is `inFlight` inside `run`.
             */
            onClick={() => {
              if (!decision.enabled) return;
              void run();
            }}
          >
            {decision.label}
          </Button>
        )}

        {/*
         * ⚠️ ONE note slot. On `/quotes` and `/invoices` it holds exactly one
         * string — {@link SAVE_SENDS_IT}, on every row — and it is the only
         * thing on the row telling the reader the record still reaches Xero
         * now that the button is gone. It is never shortened to fit.
         *
         * On an entity that still offers the button it holds either the
         * overwrite caution or the disabled reason, mutually exclusive by
         * construction (a decision is enabled or it isn't). The disabled reason
         * is provable from data in hand: no permission, the entity isn't wired,
         * the Xero document was deleted, or a status `canUpdateInXero` refuses
         * — showing it is what lets someone notice when a claim is wrong.
         *
         * All of them are VISIBLE TEXT, never a `title`: `Button` carries
         * `disabled:pointer-events-none` so a tooltip on a disabled button
         * never opens, and a tooltip is unreachable on touch regardless.
         */}
        {(note !== null || variant === 'cell') && (
          <p
            /*
             * ⚠️ THE RESERVED HEIGHT is a measured number, not `auto`. The
             * caution renders on EVERY enabled linked row, so without a
             * reserve a linked row measures 63% taller than an unlinked one
             * (132px vs 81px at 768/1280 on `/quotes`, column width
             * unaffected at 170px). The 2.85rem = 51px it reserves is
             * arithmetic: `text-2xs`/`leading-snug` gives ~15.1px per line,
             * the sentence wraps to 3 lines (~45.4px), plus the parent's
             * `gap-1.5` (6px).
             *
             * ⚠️ This does NOT equalise every row — some `statusDetail`
             * sentences run to ~7 lines (`pending`), so a disabled row can
             * still exceed the reserve. Sizing for the worst case would
             * spend 7 lines on every row to tidy a state almost none are in.
             *
             * Card variant is excluded: it's a stacked list, not a column of
             * cells, with nothing to line up.
             *
             * ⚠️ **AND `offersButton` IS NOW PART OF THE CONDITION, 2026-08-31.**
             * The reserve exists to absorb a note whose LENGTH VARIES by row.
             * On `/quotes` and `/invoices` every row now carries the identical
             * one-line {@link SAVE_SENDS_IT}, so the rows are equal on their
             * own and the reserve would only spend ~34px of blank space under
             * a short sentence on every row of both lists. Kept for the entity
             * that still varies.
             */
            className={cx(
              'm-0 text-2xs leading-snug text-fg-muted [overflow-wrap:anywhere]',
              variant === 'cell' && offersButton && 'min-h-[2.85rem]',
            )}
            /* An empty reserve is spacing, not content — keep it out of the a11y tree. */
            aria-hidden={note === null ? true : undefined}
          >
            {note}
          </p>
        )}
      </div>

      {/*
        * ⚠️ **THE CREATE CONFIRMATION WAS HERE AND IS DELETED, 2026-08-30.** It
        * read `<ConfirmDialog open={confirmingCreate} … tone="danger">` over
        * `pushConfirmCopy(entity, 'create', name)`, and its own comment said
        * *"CREATE ONLY. An update never opens this."* Nothing opens it now, so
        * it is removed rather than left unreachable — an unreachable dialog is
        * read as a live path by the next person to grep for one.
        *
        * `pushConfirmCopy`'s `'create'` branch is UNTOUCHED in `@/lib/xero` and
        * is not dead: the two form modals confirm their own create. Only this
        * list's copy of the affordance is gone.
        */}
    </>
  );
}

/**
 * Tone → the shared `Notice` tone for the outcome banner.
 *
 * UI rework (2026-10-11): the banner is a `Notice` now, so the six-way class map
 * became a tone map. `accent` lands on `info` — the accent is rationed to primary
 * actions, the active nav row and focus, and a report is none of those. An
 * exhaustive `Record`, so a seventh `XeroTone` is a build error here.
 */
const BANNER_TONES: Record<XeroTone, NoticeTone> = {
  neutral: 'neutral',
  accent: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
};

type NoticeTone = 'danger' | 'warning' | 'info' | 'success' | 'neutral';

export interface XeroPushReportBannerProps {
  report: XeroRowPushReport;
  onDismiss: () => void;
}

/**
 * What happened to a row push, rendered once so the two pages cannot describe
 * one outcome in two tones.
 *
 * Every string comes from `rowPushReport`. In particular the SERVER's own lines
 * are always rendered and are never replaced by ours: an unrecognised refusal
 * code answers `null` from `quoteRefusalOf`, which means "newer than this map"
 * rather than "not a refusal", and the server's message is then the only correct
 * thing on screen.
 *
 * `role="status"` rather than `alert`: this reports a deliberate action the user
 * has just taken and is already looking at, and three of the six outcomes are not
 * failures at all.
 */
export function XeroPushReportBanner({ report, onDismiss }: XeroPushReportBannerProps) {
  return (
    <div className="border-b border-border px-4 py-3">
      <Notice
        tone={BANNER_TONES[report.tone]}
        role="status"
        title={<span className="[overflow-wrap:anywhere]">{report.heading}</span>}
        action={
          <Button variant="ghost" size="sm" className="shrink-0" onClick={onDismiss}>
            Dismiss
          </Button>
        }
      >
        {report.lines.map((line, i) => (
          <p key={i} className="m-0 mt-1 [overflow-wrap:anywhere]">
            {line}
          </p>
        ))}
        {report.pointer && <p className="m-0 mt-1.5">{report.pointer}</p>}
      </Notice>
    </div>
  );
}
